<template>
  <div class="container-fluid">
    <div class="row page-titles">
      <div class="col-md-8">
        <breadcrumbs :current="id"></breadcrumbs>
      </div>
      <div class="col-md-4">
        <div class="float-right"><a class="btn btn-warning" target="_blank" :href="changeLink">Modify Campaign</a></div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="float-right" v-if="canEdit">
              <router-link v-if="campaign.type === 'shopify'" class="btn btn-secondary" :to="{ name: 'EditShopifyCampaign', params: { id: $route.params.id } }">
                <i class="mdi mdi-pencil"></i> Edit
              </router-link>
              <router-link v-if="campaign.type !== 'shopify'" class="btn btn-secondary" :to="{ name: 'EditCampaign', params: { id: $route.params.id } }">
                <i class="mdi mdi-pencil"></i> Edit
              </router-link>
              <router-link class="btn btn-success mr-1 ml-1" :to="{name: 'CloneCampaign', params: { id: $route.params.id, name: campaign.campaignName+' Clone' }}">
                <i class="mdi mdi-content-copy"></i> Clone
              </router-link>
              <button class="btn btn-danger btn-delete" @click="removeCampaign">
                <i class="mdi mdi-delete"></i> Delete
              </button>
            </div>
            <h4 class="card-title">{{campaign.campaignName}}<span class="badge badge-info ml-2">{{campaign.type}}</span></h4>
            <h6 class="card-subtitle" v-if="campaign.company && !campaign.whiteLabel">
              <router-link class="" :to="{ name: 'EditCompany', params: { id: campaign.company.id } }">
                {{campaign.company.companyName}}
              </router-link>
              <span v-if="campaign.dispatchTo">| {{campaign.dispatchTo}}</span>
            </h6>
          </div>
          <b-tabs nav-class="customtab">
            <reoute-tab default name="lead_form" title="Lead Form" class="card-body" v-if="canEdit && !campaign.type">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                Enter questions you want your visitors to provide answers to, including contact information (Phone or Email). You can change the order in which they will be presented to your visitors by dragging it with the mouse.
              </div>
              <lead-form :campaignId="+$route.params.id" />
              <campaign-policy :campaignId="+$route.params.id" v-if="isUserAdmin"/>
            </reoute-tab >
            <reoute-tab name="widget_config" title="Widget Config" class="card-body" v-if="canEdit && !campaign.type">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                Edit this campaign's input widget settings here. Online visitors initiate chat session with our live agents through this widget.
              </div>
              <widget-config :showSpinner="showSpinner" />
            </reoute-tab>
            <reoute-tab name="widget_code" title="Widget Code" class="card-body">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                Follow instructions below to add input widget to your website.
              </div>
              <widget-code/>
            </reoute-tab>
            <reoute-tab name="plan" title="Plan" class="card-body" v-if="isUserAdmin">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                View/Edit the campaign pricing plan
              </div>
              <campaign-plan :campaignId="+$route.params.id" />
            </reoute-tab>
            <reoute-tab name="q_a" title="Q&amp;A" class="card-body" v-if="!campaign.type">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                  Set up Frequently Asked Questions & Answers to guide your visitors through campaign questions that they may have difficulty with. For example, if you have a form question that prompts your visitor to share their phone number, you may need to explain your reasoning for doing so.
              </div>
              <campaign-questions :updatedAt="+$route.query.updatedAt" :campaignId="+$route.params.id" />
            </reoute-tab>
            <reoute-tab name="webhook" title="Webhook" class="card-body" v-if="canEdit && !campaign.type">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                Configure a webhook that will be triggered on every lead submission. This allows you to post your lead to an external service or a CRM. Contact us or your developer for assistance.
              </div>
              <webhook :campaignId="+$route.params.id"/>
            </reoute-tab>
            <reoute-tab name="landing_page" title="Landing Page" class="card-body">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                This is your campaign's landing page with the input widget built-in. You can edit the page to change design and layout to your liking. Use the Files tab to upload your own graphics.
              </div>
              <landing-page
                :campaignId="+$route.params.id"
                @created="setLandingExists"
              />
            </reoute-tab>
            <reoute-tab name="files" title="Files" class="card-body" v-if="isLandingExists && canEdit">
              <div class="alert alert-info mb-5">
                <i class="mdi mdi-information"></i>
                Upload your own graphics files for use on this campaign's landing page.
              </div>
              <files :campaignId="+$route.params.id" />
            </reoute-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import swal from 'sweetalert2';
import ConfirmButton from '@/components/ConfirmButton';
import Multiselect from '@/components/CustomMultiselect';
import CustomInput from '@/components/CustomInput';
import Files from './Files';
import Webhook from './Webhook';
import LeadForm from './LeadForm';
import LandingPage from './LandingPage';
import WidgetCode from './WidgetCode';
import WidgetConfig from './WidgetConfig';
import CampaignQuestions from './Questions';
import CampaignPolicy from './CampaignPolicy';
import CampaignPlan from './Plan';

export default {
  name: 'CampaignDetails',
  components: {
    ConfirmButton,
    LeadForm,
    Multiselect,
    LandingPage,
    CampaignQuestions,
    CustomInput,
    Files,
    Webhook,
    WidgetCode,
    WidgetConfig,
    CampaignPolicy,
    CampaignPlan
  },
  data() {
    return {
      isLandingExists: false,
      showSpinner: false,
    };
  },
  computed: {
    ...mapGetters(['getAuthUser','isUserAdmin','campaign', 'isWhiteLabel']),

    // isStaticPosition() {
    //   return get(this.campaign, 'widget.position') === 'static';
    // },

    isAdmin() {
      return this.isUserAdmin;
    },
    canEdit() {
      if (this.isUserAdmin) {
        return true;
      }
      return (this.campaign) ? this.campaign.editable: false;
    },
    changeLink() {
        return `https://docs.google.com/forms/d/e/1FAIpQLSeqSHxqd1qzQNedHIdg-7z6eAF9GRBB4EghLS_B0ShDWAkCig/viewform?usp=pp_url&entry.2039617643=${encodeURIComponent(this.getAuthUser.email)}&entry.2023113411=${encodeURIComponent(this.campaign.campaignName)}&entry.645503573=${encodeURIComponent(this.id)}`
    },
    id() {
      return this.$route.params.id;
    },
  },

  mounted() {
    this.fetchCampaign(this.$route.params.id);
  },

  watch: {
    '$route.params.id'(newId) {
      this.fetchCampaign(newId);
    }
  },

  methods: {
    ...mapActions(['fetchCampaign', 'deleteCampaign', 'trackEvent']),
    async removeCampaign() {
      const { value: isConfirmed } = await swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      });
      if (!isConfirmed) {
        return;
      }
      this.showSpinner = true;
      await this.deleteCampaign(this.$route.params.id);
      this.trackEvent({
        name: "Campaign Deleted",
        options: {
          "campaign_uid": this.$route.params.id
        }
      });
      this.$router.push({ name: 'Campaigns'});
    },

    setLandingExists(isLandingExists) {
      this.isLandingExists = isLandingExists;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Campaigns/Details/CampaignDetails.vue